import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { RouterProvider } from 'react-aria';
import { Outlet, useMatches, useNavigate, useSearchParams } from 'react-router';
import { Trans } from '@lingui/react/macro';
import { Alpha2Code } from 'i18n-iso-countries';

import type { Endpoints, Flags, PublicKeys } from '@/api';
import config from '@/config';
import Paragraph from '@/design_system/Typography/Paragraph';
import { Organization } from '@/models/organization';
import { UserFromGetSession } from '@/models/user';
import { updateSentryContext } from '@/services/sentry';
import { checkHealthAndRedirect } from '@/utils/health';
import {
  useCurrentOrganizationFromSession,
  useCurrentOrganizationFromUrl,
} from '@/utils/organization';

type AuthContextData = {
  currentSession: UserFromGetSession | null | undefined; // null -> unauthenticated, undefined -> loading
  setCurrentSession: (session: UserFromGetSession | null) => void;
  flags?: Flags;
  setFlags: (flags: Flags) => void;
  publicKeys?: PublicKeys;
  setPublicKeys: (publicKeys: PublicKeys) => void;
  geoIpCountry?: Alpha2Code;
  currentOrganization: Organization | null;
  setCurrentOrganization?: (organization: { slug: string } | null, redirectTo?: string) => void;
  clientToken?: string;
  storeToken?: string;
};

/** @public Used by Storybook */
export const AuthContext = createContext({} as AuthContextData);

const initialSessionExists = document.cookie.includes('__session_exists');

const fetchSession = () =>
  fetch(`${config.apiUrl}/sessions/current`, {
    credentials: 'include',
    headers: {
      'App-Version': config.appVersion,
      'Health-Ignore': config.health.ignore ? '1' : '0',
    },
  }).then(async (res) => {
    if (res.status === 503) {
      await checkHealthAndRedirect();
    }

    return res.json() as Promise<Endpoints['GET /sessions/current']['response']>;
  });

// Starts the session fetch as soon as possible
const sessionPromise = fetchSession().catch(async (err) => {
  if (window.location.pathname.startsWith('/health')) {
    throw err;
  }

  // If the session fetch failed, we retry after 5 seconds.
  await new Promise((resolve) => setTimeout(resolve, 5000));

  return fetchSession().catch((err) => {
    // If it still fails, we redirect to the health page.
    window.location.href = '/health';

    console.log(err);

    throw new Error('Could not fetch session after retrying.');
  });
});

type MatchHandle =
  | {
      authenticated?: boolean;
      roles?: string[];
    }
  | undefined;

export const AuthProvider = () => {
  const navigate = useNavigate();
  const matches = useMatches();

  const [currentSession, setCurrentSession] = useState<UserFromGetSession | null | undefined>(
    initialSessionExists ? undefined : null
  );

  const [flags, setFlags] = useState<Flags | undefined>();
  const [publicKeys, setPublicKeys] = useState<PublicKeys | undefined>();
  const [geoIpCountry, setGeoIpCountry] = useState<Alpha2Code | undefined>();

  const [sessionOrganization, setSessionOrganization] =
    useCurrentOrganizationFromSession(currentSession);
  const { organization: urlOrganization } = useCurrentOrganizationFromUrl();

  const [searchParams] = useSearchParams();
  const clientToken = searchParams.get('clientToken') ?? undefined;
  const storeToken = searchParams.get('storeToken') ?? undefined;

  const isAuthenticatedRoute = matches.some(
    (match) => (match.handle as MatchHandle)?.authenticated
  );

  const currentOrganization = currentSession ? sessionOrganization : urlOrganization;

  useEffect(() => {
    updateSentryContext({
      user: currentSession ?? null,
      organization: currentOrganization ?? null,
    });
  }, [currentSession, currentOrganization]);

  useEffect(() => {
    sessionPromise.then(({ user, flags, publicKeys, geoIpCountry }) => {
      // Not logged in and on an authenticated route, redirect to login page
      if (!user && isAuthenticatedRoute) {
        const redirectTo = encodeURIComponent(
          window.location.pathname + window.location.search + window.location.hash
        );

        if (redirectTo !== '%2F') {
          window.location.href = '/login?redirect=' + redirectTo;
        } else {
          window.location.href = '/login';
        }
        setCurrentSession(undefined);

        return;
      }

      // Logged in and on an unauthenticated page, redirect to home page
      if (user && !isAuthenticatedRoute && window.location.pathname !== '/mobile-upload') {
        // Or redirect to the redirect query param if it's a valid path
        if (window.location.pathname === '/login') {
          const redirectTo = decodeURIComponent(
            new URLSearchParams(window.location.search).get('redirect') ?? ''
          );

          // Prevent redirecting to domains
          if (redirectTo.match(/^\/[^/]/)) {
            window.location.href = redirectTo;

            return;
          }
        }

        window.location.href = '/';

        return;
      }

      setCurrentSession(user ?? null);
      setFlags(flags);
      setPublicKeys(publicKeys);
      setGeoIpCountry(geoIpCountry as Alpha2Code | undefined);
    });
  }, []);

  const value = useMemo(
    () => ({
      currentSession,
      setCurrentSession,
      flags,
      setFlags,
      publicKeys,
      setPublicKeys,
      geoIpCountry,
      currentOrganization,
      setCurrentOrganization: currentSession ? setSessionOrganization : undefined,
      clientToken,
      storeToken,
    }),
    [
      clientToken,
      storeToken,
      currentSession,
      flags,
      publicKeys,
      geoIpCountry,
      sessionOrganization,
      setSessionOrganization,
      urlOrganization,
    ]
  );

  if (currentSession === undefined || (currentSession === null && isAuthenticatedRoute)) {
    return <AuthLoader />;
  }

  return (
    <RouterProvider navigate={navigate}>
      <AuthContext value={value}>
        <Outlet />
      </AuthContext>
    </RouterProvider>
  );
};

export const useCurrentSession = () => {
  const { currentSession, setCurrentSession } = useContext(AuthContext);

  return {
    currentSession,
    setCurrentSession,
  };
};

export const useFlags = () => {
  const { flags, setFlags } = useContext(AuthContext);

  return { flags: flags!, setFlags };
};

export const usePublicKeys = () => {
  const { publicKeys, setPublicKeys } = useContext(AuthContext);

  return { publicKeys: publicKeys!, setPublicKeys };
};

export const useGeoIpCountry = () => useContext(AuthContext).geoIpCountry;

export const useCurrentOrganization = () => {
  const { currentOrganization, setCurrentOrganization } = useContext(AuthContext);

  return [currentOrganization, setCurrentOrganization] as const;
};

export const useClientToken = () => {
  const { clientToken } = useContext(AuthContext);

  return clientToken;
};

export const useStoreToken = () => {
  const { storeToken } = useContext(AuthContext);

  return storeToken;
};

const AuthLoader = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), 3000);

    return () => clearTimeout(timeout);
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <Paragraph>
        <Trans id="auth.loading">Loading...</Trans>
      </Paragraph>
      <div className="auth-loader" />
    </div>
  );
};
